/** The app's "Next game" tab, on the web. */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { dateTime, relative, shortDate, type MyNextGame } from '@lcc/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, Empty, ErrorNote, Loading, Pill } from '../components/ui';
import { PageHeader } from '../App';

interface Duty {
  id: string;
  title: string;
  kind: string;
  occursOn: string;
  assignments: Array<{ memberId: string; fullName: string; status: string }>;
}

export function NextGamePage() {
  const { user } = useAuth();

  const next = useQuery({
    queryKey: ['my-next'],
    queryFn: () => api.get<{ data: MyNextGame[] }>('/games/mine/next').then((r) => r.data),
  });
  const duties = useQuery({
    queryKey: ['my-duties'],
    queryFn: () => api.get<{ data: Duty[] }>('/duties/mine').then((r) => r.data),
  });

  const games = next.data ?? [];
  const [first, ...rest] = games;

  return (
    <>
      <PageHeader
        title={`Hello, ${user?.fullName?.split(' ')[0] ?? 'there'}`}
        sub="Your next game, and anything you are down for"
      />
      <div className="content stack">
        {next.isLoading && <Loading what="Finding your next game" />}
        {next.error && <ErrorNote error={next.error} />}

        {!next.isLoading && !next.error && !first && (
          <Card>
            <Empty title="No games coming up">
              Nothing on your schedule yet. Your convenor posts draws before each schedule
              starts — the <Link to="/schedule">schedule</Link> shows the whole club&rsquo;s.
            </Empty>
          </Card>
        )}

        {first && (
          <Card title="Your next game" flush>
            <div className="fx">
              <p className="fx__league">{first.leagueName}</p>
              <p className="fx__when">{relative(first.scheduledAt)}</p>
              <p className="fx__date">{dateTime(first.scheduledAt)}</p>

              <div className="fx__chips">
                {first.sheetNumber !== null && (
                  <span className="fx__chip fx__chip--sheet">Sheet <b>{first.sheetNumber}</b></span>
                )}
                {first.myPosition && (
                  <span className="fx__chip">You play <b>{first.myPosition}</b></span>
                )}
                <span className="fx__chip">
                  {first.myTeamName} <b>v</b> {first.opponentTeamName ?? 'TBD'}
                </span>
              </div>

              <div className="fx__teams">
                <h3>{first.myTeamName}</h3>
                <p className="fx__vs">versus {first.opponentTeamName ?? 'to be decided'}</p>
              </div>
            </div>

            <div className="table-wrap">
              <table className="t">
                <caption className="sr-only">Your line-up</caption>
                <thead><tr><th>Position</th><th>Player</th></tr></thead>
                <tbody>
                  {first.lineup.map((p) => (
                    <tr key={p.fullName}>
                      <td style={{ textTransform: 'capitalize' }} className="muted">{p.position}</td>
                      <td style={{ fontWeight: p.fullName === user?.fullName ? 700 : 400 }}>
                        {p.fullName}
                        {p.fullName === user?.fullName && <> <Pill tone="info">you</Pill></>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {rest.length > 0 && (
          <Card title="After that" flush>
            <div className="table-wrap">
              <table className="t">
                <caption className="sr-only">Your later games</caption>
                <thead>
                  <tr>
                    <th>When</th><th>League</th><th className="num">Sheet</th>
                    <th>Your team</th><th>Opponent</th>
                  </tr>
                </thead>
                <tbody>
                  {rest.map((g) => (
                    <tr key={g.gameId}>
                      <td className="nowrap">{dateTime(g.scheduledAt)}</td>
                      <td>{g.leagueName}</td>
                      <td className="num mono">{g.sheetNumber ?? '—'}</td>
                      <td>{g.myTeamName}</td>
                      <td>{g.opponentTeamName ?? 'TBD'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {duties.data && duties.data.length > 0 && (
          <Card title="You are down for" flush>
            <div className="table-wrap">
              <table className="t">
                <caption className="sr-only">Duties assigned to you</caption>
                <thead><tr><th>When</th><th>What</th><th>Kind</th><th>Status</th></tr></thead>
                <tbody>
                  {duties.data.map((d) => {
                    const mine = d.assignments.find((a) => a.memberId === user?.memberId);
                    return (
                      <tr key={d.id}>
                        <td className="nowrap">{shortDate(d.occursOn)}</td>
                        <td>{d.title}</td>
                        <td className="muted">{d.kind.replace(/_/g, ' ')}</td>
                        <td>
                          <Pill tone={mine?.status === 'confirmed' ? 'good' : 'warn'}>
                            {mine?.status ?? 'assigned'}
                          </Pill>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
